import { useMutation, type UseMutationOptions, useQueryClient } from "@tanstack/react-query";

import { authClient, betterAuthErrorText } from "lib/auth/authClient";
import { BetterAuthError } from "../utils";
import { SSO_PROVIDERS_QUERY_KEY, type SsoProviderResponse } from "./useSsoProviders";

type VerifyDomainInput = {
  providerId: string;
};

export function useVerifyDomain(
  options?: UseMutationOptions<unknown, BetterAuthError, VerifyDomainInput, unknown>,
) {
  const queryClient = useQueryClient();
  const { onSuccess, ...rest } = options ?? {};

  return useMutation({
    ...rest,
    mutationFn: async ({ providerId }: VerifyDomainInput) => {
      const { data, error } = await authClient.sso.verifyDomain({ providerId });
      if (error) {
        throw new Error(betterAuthErrorText(error, "Could not verify domain"));
      }
      return data;
    },
    onSuccess: (_data, { providerId }, ...rest) => {
      queryClient.setQueryData<SsoProviderResponse[]>(SSO_PROVIDERS_QUERY_KEY, (prev) =>
        prev?.map((p) => (p.providerId === providerId ? { ...p, domainVerified: true } : p)),
      );
      onSuccess?.(_data, { providerId }, ...rest);
    },
  });
}
